/**
 * Editar ligações — o pinout como caixas e fios, em vez de nomes digitados.
 *
 * **Por que existe.** A junção não é um registro: é uma convenção de texto. Dois
 * pinos do mesmo componente viram a mesma junção quando o circuito deles começa
 * igual antes do ponto e termina com a mesma etiqueta `#` — e o componente
 * precisa estar marcado como tipo 3. São duas escritas que precisam concordar,
 * feitas à mão, e errar não dá erro: dá outro desenho. Aqui o operador aponta e
 * o sistema escreve as duas.
 *
 * **O que é leitura e o que é escrita.** Abrir, selecionar, arrastar, dar zoom e
 * digitar só leem. Escrever acontece em UM botão: "Gravar" o lote
 * (`POST .../grafo/aplicar`). "Simular" passa pelo mesmo caminho e desfaz.
 *
 * **O lote.** Tudo vai para ele: fio novo (do zero, de uma caixa vazia), ligação
 * nova a partir de um pino, apagar linha, ligar a um pino existente, marcar
 * junção, soltar etiqueta. É gravado junto, numa transação no servidor que confere
 * o resultado com o motor e desfaz tudo se a base piorar. Até 25/09/2026 criar e
 * apagar gravavam na hora, fora do lote e sem conferência.
 *
 * **O grafo não é o desenho de 18 segundos.** É a mesma árvore que o motor monta
 * para desenhar (medido: todo pino desenhado está nela), sem o SVG. Continua
 * custando o que o motor custa — uma consulta por pino —, então a tela conta o
 * tempo e deixa desistir, como a do desenho.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { ErroDaApi } from "@/api/erros"
import {
  aplicarEdicao, buscarGrafo, usarRestricoesDoModelo,
  type ArestaDoGrafo, type GrafoDoPinout, type MarcaDoNo, type NoDoGrafo,
  type OperacaoDoEditor, type PedidoDoGrafo, type ResultadoDaEdicao,
} from "@/api/editorDeLigacoes"
import { GavetaDeCadastro, type AlvoDeEdicao } from "../GavetaDeCadastro"
import { AssistenteDeJuncao } from "./AssistenteDeJuncao"
import { FormularioDoFio, type ModoDoFio } from "./FormularioDoFio"
import {
  CABECALHO, LINHA, base, etiqueta, montarLayout, temEtiqueta, tracado, verificar, type Caixa,
} from "./regras"

type Selecao = { tipo: "pino"; id: string } | { tipo: "caixa"; componenteId: number } | null
type Pendente = { op: OperacaoDoEditor; texto: string }

/** O que a tela precisa de fora. As funções têm padrão; o harness de conferência troca por fixture. */
export type EditorProps = {
  projetoId: number
  componenteId: number
  modeloId: number
  simplificarAterramento: boolean
  aoFechar: () => void
  /** Algo foi gravado: o desenho em SVG da tela ficou velho. */
  aoAlterou?: () => void
  carregar?: (p: PedidoDoGrafo, sinal?: AbortSignal) => Promise<GrafoDoPinout>
  gravar?: typeof aplicarEdicao
}

const COR_DA_MARCA: Record<MarcaDoNo, { fundo: string; texto: string; rotulo: string; explica: string }> = {
  redeLargaGrave: {
    fundo: "var(--erro-soft)", texto: "var(--erro-on-soft)", rotulo: "descrição vazia",
    explica: "circuito sem nome: o motor usa o nome como padrão, e vazio casa o projeto inteiro",
  },
  curinga: {
    fundo: "var(--erro-soft)", texto: "var(--erro-on-soft)", rotulo: "fio por curinga",
    explica: "o _ do nome vira curinga na busca do motor e desenha um fio que não existe",
  },
  redeLarga: {
    fundo: "var(--aviso-soft)", texto: "var(--aviso-on-soft)", rotulo: "rede larga",
    explica: "o nome é começo de 20 ou mais circuitos, e a expansão puxa todos",
  },
  juncaoOrfa: {
    fundo: "var(--aviso-soft)", texto: "var(--aviso-on-soft)", rotulo: "junção órfã",
    explica: "tem # mas nenhum pino forma junção com ele: a junção não se forma",
  },
}

const COR_DA_ARESTA: Record<ArestaDoGrafo["tipo"], string> = {
  ligacao: "var(--txt3)",
  juncao: "var(--brand)",
  intermediario: "var(--txt3)",
  aterramento: "var(--ok)",
}

function chavePosicoes(projetoId: number, componenteId: number) {
  return `sistema-oc.editor-ligacoes.posicoes.${projetoId}.${componenteId}`
}

function lerPosicoes(chave: string): Record<number, { x: number; y: number }> {
  try {
    const g = localStorage.getItem(chave)
    return g ? JSON.parse(g) : {}
  } catch {
    return {}
  }
}

function chaveFonte(projetoId: number) {
  return `sistema-oc.editor-ligacoes.fonte.${projetoId}`
}

function lerFonte(chave: string): string {
  try { return localStorage.getItem(chave) ?? "" } catch { return "" }
}

function mensagemDe(e: unknown): string {
  if (e instanceof ErroDaApi) {
    if (e.ehSemPermissao) return "Sem permissão para isto. Apagar linha pede a chave de excluir do projeto."
    return e.message
  }
  return e instanceof Error ? e.message : "Não foi possível completar."
}

export function EditorDeLigacoes({
  projetoId, componenteId, modeloId, simplificarAterramento, aoFechar, aoAlterou,
  carregar = buscarGrafo, gravar = aplicarEdicao,
}: EditorProps) {
  const [grafo, setGrafo] = useState<GrafoDoPinout | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [decorrido, setDecorrido] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [selecao, setSelecao] = useState<Selecao>(null)
  const [ligandoDe, setLigandoDe] = useState<string | null>(null)
  const [pendentes, setPendentes] = useState<Pendente[]>([])
  const [aviso, setAviso] = useState<string | null>(null)
  const [resultado, setResultado] = useState<ResultadoDaEdicao | null>(null)
  const [trabalhando, setTrabalhando] = useState<string | null>(null)
  // null = ninguém escolheu ainda; 0 = todos os modelos (Q6). Não começa em "todos": seria escolher calado.
  const [restricaoId, setRestricaoId] = useState<number | null>(null)
  const [fonte, setFonte] = useState(() => lerFonte(chaveFonte(projetoId)))
  const [formulario, setFormulario] = useState<ModoDoFio | null>(null)
  // O assistente da junção nova; `componente` quando ela nasce numa caixa da tela.
  const [assistente, setAssistente] = useState<{ componente: { id: number; codigo: string } | null } | null>(null)
  // O assistente pediu "o próximo pino clicado é a ponta deste ramo".
  const pedidoDePino = useRef<((no: NoDoGrafo) => void) | null>(null)
  const [pedindoPino, setPedindoPino] = useState(false)
  // Editar componente, conector ou circuito: a MESMA gaveta de cadastro da aba Gráfico e da tela de Cadastros.
  const [cadastro, setCadastro] = useState<{ alvo: AlvoDeEdicao; contexto: string } | null>(null)
  const chave = chavePosicoes(projetoId, componenteId)
  const [posicoes, setPosicoes] = useState<Record<number, { x: number; y: number }>>(() => lerPosicoes(chave))
  const cancelador = useRef<AbortController | null>(null)

  const restricoes = usarRestricoesDoModelo(projetoId, modeloId)
  const restricaoRotulo = restricoes.itens.find((r) => r.id === restricaoId)?.descricao ?? ""

  async function recarregar() {
    cancelador.current?.abort()
    const c = new AbortController()
    cancelador.current = c
    setCarregando(true)
    setDecorrido(0)
    setErro(null)
    try {
      const g = await carregar({ projetoId, componenteId, modeloId, simplificarAterramento }, c.signal)
      setGrafo(g)
      setSelecao((s) => (s?.tipo === "pino" && !g.nos.some((n) => n.id === s.id) ? null : s))
    } catch (e) {
      if (!c.signal.aborted) setErro(mensagemDe(e))
    } finally {
      if (cancelador.current === c) { setCarregando(false); cancelador.current = null }
    }
  }

  // Abrir o editor é o pedido: carrega na hora. Trocar componente, modelo ou aterramento
  // é outro grafo — e a fila de alterações era do anterior.
  useEffect(() => {
    setPendentes([])
    setResultado(null)
    setSelecao(null)
    void recarregar()
    return () => cancelador.current?.abort()
  }, [projetoId, componenteId, modeloId, simplificarAterramento])

  useEffect(() => {
    if (!carregando) return
    const t = setInterval(() => setDecorrido((s) => s + 0.1), 100)
    return () => clearInterval(t)
  }, [carregando])

  useEffect(() => {
    try { localStorage.setItem(chave, JSON.stringify(posicoes)) } catch { /* sem armazenamento */ }
  }, [chave, posicoes])

  useEffect(() => {
    try { localStorage.setItem(chaveFonte(projetoId), fonte) } catch { /* sem armazenamento */ }
  }, [projetoId, fonte])

  useEffect(() => {
    function tecla(e: KeyboardEvent) {
      if (e.key !== "Escape") return
      if (pedidoDePino.current) { pedidoDePino.current = null; setPedindoPino(false) }
      else if (ligandoDe) setLigandoDe(null)
      else if (formulario) setFormulario(null)
      else if (assistente) setAssistente(null)
      else setSelecao(null)
    }
    window.addEventListener("keydown", tecla)
    return () => window.removeEventListener("keydown", tecla)
  }, [ligandoDe, formulario, assistente])

  const layout = useMemo(() => (grafo ? montarLayout(grafo, posicoes) : null), [grafo, posicoes])
  const porId = useMemo(() => new Map((grafo?.nos ?? []).map((n) => [n.id, n])), [grafo])
  const pinoSelecionado = selecao?.tipo === "pino" ? porId.get(selecao.id) ?? null : null
  const caixaSelecionada = selecao?.tipo === "caixa"
    ? layout?.caixas.find((c) => c.componenteId === selecao.componenteId) ?? null
    : null

  // --------------------------------------------------------------- arrastar caixas
  const arrasto = useRef<{ componenteId: number; x0: number; y0: number; cx: number; cy: number; moveu: boolean } | null>(null)

  function comecarArrasto(e: React.PointerEvent, caixa: Caixa) {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    arrasto.current = { componenteId: caixa.componenteId, x0: e.clientX, y0: e.clientY, cx: caixa.x, cy: caixa.y, moveu: false }
  }
  function mover(e: React.PointerEvent) {
    const a = arrasto.current
    if (!a) return
    const dx = (e.clientX - a.x0) / zoom
    const dy = (e.clientY - a.y0) / zoom
    if (!a.moveu && Math.abs(dx) + Math.abs(dy) < 4) return
    a.moveu = true
    setPosicoes((p) => ({ ...p, [a.componenteId]: { x: Math.max(0, a.cx + dx), y: Math.max(0, a.cy + dy) } }))
  }
  function soltar() {
    const a = arrasto.current
    arrasto.current = null
    // Clique sem arrastar é selecionar a caixa.
    if (a && !a.moveu) setSelecao({ tipo: "caixa", componenteId: a.componenteId })
  }

  // --------------------------------------------------------------- o lote
  function enfileirar(op: OperacaoDoEditor, texto: string) {
    enfileirarVarios([{ op, texto }])
  }

  /** Vários de uma vez — um fio de três pontas são três pinos. Tudo ou nada. */
  function enfileirarVarios(itens: Pendente[]) {
    if (!grafo) return
    for (const i of itens) {
      const motivo = verificar(grafo, i.op)
      if (motivo) { setAviso(motivo); return }
    }
    setAviso(null)
    setResultado(null)
    setPendentes((l) => [...l, ...itens])
  }

  function clicarPino(no: NoDoGrafo) {
    if (pedidoDePino.current) {
      const entregar = pedidoDePino.current
      pedidoDePino.current = null
      setPedindoPino(false)
      if (no.pinoutId > 0) entregar(no)
      return
    }
    if (ligandoDe) {
      const de = porId.get(ligandoDe)
      setLigandoDe(null)
      if (!de) return
      enfileirar(
        { tipo: "ligarExistente", deId: de.pinoutId, paraId: no.pinoutId },
        `ligar ${no.componenteCodigo || no.componente} pino ${no.pino} ao circuito '${de.circuito}' (de ${de.componenteCodigo || de.componente} pino ${de.pino})`,
      )
      setSelecao({ tipo: "pino", id: no.id })
      return
    }
    setSelecao({ tipo: "pino", id: no.id })
  }

  async function gravarLote(simular: boolean) {
    if (pendentes.length === 0) return
    setTrabalhando(simular ? "Simulando…" : "Gravando…")
    setAviso(null)
    try {
      const r = await gravar({
        projetoId, modeloId, simplificarAterramento, simular, operacoes: pendentes.map((p) => p.op),
      })
      setResultado(r)
      if (r.aplicado) {
        setPendentes([])
        aoAlterou?.()
        await recarregar()
      }
    } catch (e) {
      setAviso(mensagemDe(e))
    } finally {
      setTrabalhando(null)
    }
  }

  // --------------------------------------------------------------- desenho
  const selecionadoId = pinoSelecionado?.id ?? null
  const repetidos = new Set(grafo?.repetidos ?? [])

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 10,
          padding: "8px 18px", background: "var(--brand-soft)", borderBottom: "1px solid var(--line2)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <b style={{ fontSize: 13, color: "var(--brand-on-soft)" }}>Editar ligações</b>
          <span style={{ fontSize: 11.5, color: "var(--brand-on-soft)" }}>
            aponte os pinos; o sistema escreve o nome dos circuitos e a marca de junção
          </span>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 260 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
            Restrição — vale para tudo que você criar aqui
          </span>
          <select
            className="campo"
            value={restricaoId === null ? "" : String(restricaoId)}
            onChange={(e) => setRestricaoId(e.target.value === "" ? null : Number(e.target.value))}
            aria-label="Restrição das ligações novas"
            title="Só as restrições deste modelo: com outra, o pino criado sumiria deste desenho."
            style={{
              width: "100%", height: 30, padding: "0 9px", borderRadius: 7, fontSize: 12.5,
              border: restricaoId !== null ? "1px solid var(--line2)" : "1px solid var(--acao)",
              background: "var(--surface)",
            }}
          >
            <option value="">— escolha a restrição —</option>
            <option value="0">Todos os modelos (sem restrição)</option>
            {restricoes.itens.map((r) => <option key={r.id} value={r.id}>{r.descricao}</option>)}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 180 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>Fonte (arquivo original)</span>
          <input
            className="campo"
            value={fonte}
            onChange={(e) => setFonte(e.target.value)}
            aria-label="Fonte das ligações novas"
            placeholder="de onde vem o pinout"
            style={{ height: 30, padding: "0 9px", borderRadius: 7, fontSize: 12.5, border: "1px solid var(--line2)", background: "var(--surface)" }}
          />
        </label>

        <span style={{ flex: 1 }} />

        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <BotaoPequeno aoClicar={() => setZoom((z) => Math.max(0.3, +(z - 0.1).toFixed(2)))} titulo="Diminuir">−</BotaoPequeno>
          <span style={{ fontSize: 11.5, width: 40, textAlign: "center", color: "var(--txt2)" }}>{Math.round(zoom * 100)}%</span>
          <BotaoPequeno aoClicar={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} titulo="Aumentar">+</BotaoPequeno>
          <BotaoPequeno aoClicar={() => setPosicoes({})} titulo="Voltar as caixas para o arranjo automático">arrumar</BotaoPequeno>
        </div>

        <BotaoPequeno aoClicar={() => { setFormulario(null); setSelecao(null); setAssistente({ componente: null }) }}
                      titulo="O assistente monta a junção: base, ramos e pontas — ninguém digita #">
          Nova junção…
        </BotaoPequeno>
        <BotaoPequeno aoClicar={() => (carregando ? cancelador.current?.abort() : void recarregar())}>
          {carregando ? `Cancelar · ${decorrido.toFixed(1)} s` : "Recarregar"}
        </BotaoPequeno>
        <BotaoPequeno aoClicar={aoFechar}>Fechar o editor</BotaoPequeno>
      </div>

      {pedindoPino && (
        <div role="status" style={{
          padding: "7px 18px", background: "var(--acao-soft)", color: "var(--acao-on-soft)", fontSize: 12.5,
          display: "flex", gap: 10, alignItems: "center",
        }}>
          <b>Clique no pino que é a ponta deste ramo.</b>
          <span>Esc cancela.</span>
          <BotaoPequeno aoClicar={() => { pedidoDePino.current = null; setPedindoPino(false) }}>Cancelar</BotaoPequeno>
        </div>
      )}

      {ligandoDe && (
        <div role="status" style={{
          padding: "7px 18px", background: "var(--acao-soft)", color: "var(--acao-on-soft)", fontSize: 12.5,
          display: "flex", gap: 10, alignItems: "center",
        }}>
          <b>Clique no pino que vai receber o circuito '{porId.get(ligandoDe)?.circuito}'.</b>
          <span>Ele sai do circuito atual. Esc cancela.</span>
          <BotaoPequeno aoClicar={() => setLigandoDe(null)}>Cancelar</BotaoPequeno>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div
          style={{ flex: 1, minWidth: 0, overflow: "auto", background: "var(--surface3)", position: "relative" }}
          onPointerMove={mover}
          onPointerUp={soltar}
          onPointerLeave={soltar}
        >
          {carregando && !grafo && (
            <Centro>
              <b>Montando o grafo… {decorrido.toFixed(1)} s</b>
              <span style={{ fontSize: 12, color: "var(--txt3)", maxWidth: "50ch" }}>
                É o mesmo trabalho do motor que desenha — uma consulta por pino. Componente grande leva
                alguns segundos, como o desenho.
              </span>
            </Centro>
          )}
          {erro && !carregando && (
            <Centro>
              <b style={{ color: "var(--erro)" }}>{erro}</b>
              <span style={{ fontSize: 12, color: "var(--txt3)" }}>Confira o modelo e tente de novo.</span>
            </Centro>
          )}
          {grafo && layout && grafo.nos.length === 0 && (
            <Centro>
              <div style={{
                width: 232, borderRadius: 7, border: "2px dashed var(--brand)", background: "var(--surface)", overflow: "hidden", textAlign: "left",
              }}>
                <div style={{ padding: "6px 9px", background: "var(--surface2)", fontSize: 12, fontWeight: 700, color: "var(--txt)" }}>
                  {grafo.componenteCodigo || grafo.componente || `Componente ${componenteId}`}
                </div>
                <div style={{ padding: "10px 9px", fontSize: 11.5, color: "var(--txt3)" }}>nenhum pino neste modelo, ainda</div>
              </div>
              <BotaoPequeno aoClicar={() => {
                setSelecao(null)
                setFormulario({ tipo: "componente", componenteId, codigo: grafo.componenteCodigo || grafo.componente || String(componenteId) })
              }}>
                Novo fio saindo daqui
              </BotaoPequeno>
            </Centro>
          )}
          {grafo && layout && grafo.nos.length > 0 && (
            <svg
              width={layout.largura * zoom}
              height={layout.altura * zoom}
              viewBox={`0 0 ${layout.largura} ${layout.altura}`}
              role="img"
              aria-label={`Ligações de ${grafo.nos.length} pinos`}
              style={{ display: "block", userSelect: "none", opacity: carregando ? 0.5 : 1 }}
            >
              <g>
                {grafo.arestas.map((a) => {
                  const d = tracado(layout, a)
                  if (!d) return null
                  const tocada = selecionadoId !== null && (a.de === selecionadoId || a.para === selecionadoId)
                  return (
                    <path
                      key={`${a.de}-${a.para}-${a.tipo}`}
                      d={d}
                      fill="none"
                      stroke={a.curinga ? "var(--erro)" : COR_DA_ARESTA[a.tipo]}
                      strokeWidth={tocada ? 2.6 : a.curinga ? 2 : 1.3}
                      strokeDasharray={a.curinga ? "6 4" : a.tipo === "intermediario" ? "3 3" : undefined}
                      opacity={selecionadoId && !tocada ? 0.35 : 0.9}
                    >
                      <title>
                        {a.curinga ? "Fio que só existe por curinga (_) — não existe no carro. " : ""}
                        {a.tipo}: {porId.get(a.de)?.circuito} → {porId.get(a.para)?.circuito}
                      </title>
                    </path>
                  )
                })}
              </g>

              {layout.caixas.map((c) => (
                <g key={c.componenteId}>
                  <rect
                    x={c.x} y={c.y} width={c.largura} height={c.altura} rx={7}
                    fill="var(--surface)"
                    stroke={caixaSelecionada?.componenteId === c.componenteId ? "var(--acao)" : c.raiz ? "var(--brand)" : "var(--line2)"}
                    strokeWidth={c.raiz || caixaSelecionada?.componenteId === c.componenteId ? 2 : 1}
                  />
                  <g
                    onPointerDown={(e) => comecarArrasto(e, c)}
                    style={{ cursor: "grab" }}
                  >
                    <rect x={c.x} y={c.y} width={c.largura} height={CABECALHO} rx={7}
                          fill={c.tipo === 3 ? "var(--brand-soft)" : "var(--surface2)"} />
                    <text x={c.x + 9} y={c.y + 19} fontSize={12} fontWeight={700} fill="var(--txt)">
                      {cortar(c.codigo || c.titulo, 22)}
                    </text>
                    {c.tipo === 3 && (
                      <text x={c.x + c.largura - 9} y={c.y + 19} fontSize={10} fontWeight={700}
                            fill="var(--brand-on-soft)" textAnchor="end">JUNÇÃO</text>
                    )}
                    <title>{c.titulo} — arraste para mover, clique para selecionar</title>
                  </g>

                  {c.pinos.map(({ no, y }) => {
                    const marca = no.achados[0]
                    const cor = marca ? COR_DA_MARCA[marca] : null
                    const selecionado = no.id === selecionadoId
                    return (
                      <g key={no.id} onClick={() => clicarPino(no)} style={{ cursor: ligandoDe || pedindoPino ? "crosshair" : "pointer" }}>
                        <rect
                          x={c.x + 1} y={y - LINHA / 2 + 1} width={c.largura - 2} height={LINHA - 2}
                          fill={selecionado ? "var(--brand-soft)" : cor ? cor.fundo : "transparent"}
                        />
                        <text x={c.x + 9} y={y + 4} fontSize={11} fill="var(--txt2)">
                          {cortar(`${no.conector} · ${no.pino}`, 14)}
                        </text>
                        <text x={c.x + c.largura - (c.juncoes.length > 0 ? 34 : 9)} y={y + 4} fontSize={11}
                              fontFamily="var(--fm)" textAnchor="end"
                              fill={cor ? cor.texto : "var(--txt)"}>
                          {cortar(no.circuito === "" ? "(vazio)" : no.circuito, 16)}
                        </text>
                        {repetidos.has(no.id) && (
                          <text x={c.x + 118} y={y + 4} fontSize={9.5} fontWeight={700} fill="var(--acao)">
                            ×{no.visitas}
                          </text>
                        )}
                        <title>
                          {no.componente} · {no.conector} pino {no.pino} — circuito '{no.circuito}'{no.cor ? ` (${no.cor.trim()})` : ""}
                          {no.achados.map((m) => `\n⚠ ${COR_DA_MARCA[m].rotulo}: ${COR_DA_MARCA[m].explica}`).join("")}
                          {no.visitas > 1 ? `\nO motor chegou aqui ${no.visitas} vezes, por caminhos diferentes.` : ""}
                        </title>
                      </g>
                    )
                  })}

                  {c.juncoes.map((j) => (
                    <g key={j.chave}>
                      <path
                        d={`M ${c.x + c.largura - 22} ${j.de} h 8 V ${j.ate} h -8`}
                        fill="none" stroke="var(--brand)" strokeWidth={2}
                      />
                      <text x={c.x + c.largura - 11} y={(j.de + j.ate) / 2 + 3} fontSize={9} fontWeight={700}
                            fill="var(--brand)" textAnchor="middle"
                            transform={`rotate(-90 ${c.x + c.largura - 11} ${(j.de + j.ate) / 2})`}>
                        {j.etiqueta}
                      </text>
                      <title>Os pinos amarrados aqui são a mesma junção ({j.etiqueta}).</title>
                    </g>
                  ))}
                </g>
              ))}
            </svg>
          )}
        </div>

        {cadastro ? (
          <GavetaDeCadastro
            projetoId={projetoId}
            alvo={cadastro.alvo}
            contexto={cadastro.contexto}
            aoFechar={() => setCadastro(null)}
            aoSalvou={() => { aoAlterou?.(); void recarregar() }}
          />
        ) : (
        <aside
          aria-label="Painel do editor"
          style={{
            width: 350, flex: "0 0 auto", overflow: "auto", borderLeft: "1px solid var(--line2)",
            background: "var(--surface)", padding: 14, display: "flex", flexDirection: "column", gap: 14,
          }}
        >
          {grafo && <Resumo grafo={grafo} />}

          {aviso && (
            <div role="alert" style={{
              padding: 10, borderRadius: 7, background: "var(--erro-soft)", color: "var(--erro-on-soft)", fontSize: 12.5,
            }}>
              {aviso}
            </div>
          )}

          {assistente && grafo && (
            <AssistenteDeJuncao
              key={assistente.componente?.id ?? "livre"}
              projetoId={projetoId}
              grafo={grafo}
              componente={assistente.componente}
              restricaoId={restricaoId}
              restricaoRotulo={restricaoRotulo}
              fonte={fonte}
              aoPedirPino={(quando) => { pedidoDePino.current = quando; setPedindoPino(true); setAviso(null) }}
              aoPorNoLote={(op, texto) => { enfileirar(op, texto); setAssistente(null) }}
              aoCancelar={() => { pedidoDePino.current = null; setPedindoPino(false); setAssistente(null) }}
            />
          )}

          {!assistente && formulario && grafo && (
            <FormularioDoFio
              key={formulario.tipo === "pino" ? formulario.no.id : `c${formulario.componenteId}`}
              projetoId={projetoId}
              modo={formulario}
              restricaoId={restricaoId}
              restricaoRotulo={restricaoRotulo}
              fonte={fonte}
              aoPorNoLote={(itens) => { enfileirarVarios(itens); setFormulario(null) }}
              aoCancelar={() => setFormulario(null)}
            />
          )}

          {!assistente && !formulario && pinoSelecionado && grafo && (
            <PainelDoPino
              key={pinoSelecionado.id}
              no={pinoSelecionado}
              grafo={grafo}
              trabalhando={trabalhando}
              aoNovaLigacao={() => { setFormulario({ tipo: "pino", no: pinoSelecionado }); setAviso(null) }}
              aoEditarCadastro={(entidade) => setCadastro({
                alvo: { entidade, id: entidade === "conectores" ? pinoSelecionado.conectorId : pinoSelecionado.circuitoId },
                contexto: entidade === "conectores"
                  ? `Conector de ${pinoSelecionado.componenteCodigo || pinoSelecionado.componente}`
                  : `Fio de ${pinoSelecionado.componenteCodigo || pinoSelecionado.componente} ${pinoSelecionado.conector}·${pinoSelecionado.pino} — vale para todas as pontas dele`,
              })}
              aoLigarExistente={() => { setLigandoDe(pinoSelecionado.id); setAviso(null) }}
              aoSoltar={() => enfileirar(
                { tipo: "soltarEtiqueta", pinoutId: pinoSelecionado.pinoutId },
                `soltar a etiqueta de '${pinoSelecionado.circuito}' — passa a '${base(pinoSelecionado.circuito)}'`,
              )}
              aoApagar={() => enfileirar(
                { tipo: "apagarPino", pinoutId: pinoSelecionado.pinoutId },
                `apagar a linha ${pinoSelecionado.componenteCodigo || pinoSelecionado.componente} ${pinoSelecionado.conector}·${pinoSelecionado.pino} (fio '${pinoSelecionado.circuito}')`,
              )}
            />
          )}

          {!assistente && !formulario && caixaSelecionada && grafo && (
            <PainelDaCaixa
              key={caixaSelecionada.componenteId}
              caixa={caixaSelecionada}
              aoEditarComponente={() => setCadastro({
                alvo: { entidade: "componentes", id: caixaSelecionada.componenteId },
                contexto: "Componente do desenho — vale para todo pino dele",
              })}
              aoNovaJuncao={() => setAssistente({
                componente: { id: caixaSelecionada.componenteId, codigo: caixaSelecionada.codigo || caixaSelecionada.titulo },
              })}
              aoNovoFio={() => setFormulario({
                tipo: "componente", componenteId: caixaSelecionada.componenteId, codigo: caixaSelecionada.codigo || caixaSelecionada.titulo,
              })}
              aoMarcar={(ids) => enfileirar(
                { tipo: "marcarJuncao", componenteId: caixaSelecionada.componenteId, pinoutIds: ids },
                `marcar ${caixaSelecionada.codigo || caixaSelecionada.titulo} como junção, com ${ids.length} ramos`,
              )}
            />
          )}

          {!assistente && !formulario && !pinoSelecionado && !caixaSelecionada && grafo && (
            <ListaDeAchados grafo={grafo} aoEscolher={(id) => setSelecao({ tipo: "pino", id })} />
          )}

          <Lote
            pendentes={pendentes}
            resultado={resultado}
            trabalhando={trabalhando}
            aoTirar={(i) => { setPendentes((l) => l.filter((_, j) => j !== i)); setResultado(null) }}
            aoSimular={() => void gravarLote(true)}
            aoGravar={() => void gravarLote(false)}
          />
        </aside>
        )}
      </div>
    </div>
  )
}

// ================================================================= o painel

function Resumo({ grafo }: { grafo: GrafoDoPinout }) {
  const contagem = new Map<MarcaDoNo, number>()
  for (const n of grafo.nos) for (const m of n.achados) contagem.set(m, (contagem.get(m) ?? 0) + 1)
  const curingas = grafo.arestas.filter((a) => a.curinga).length
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
        {grafo.nos.length} pinos · {grafo.arestas.length} ligações · montado em {grafo.segundos.toFixed(1)} s
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {(Object.keys(COR_DA_MARCA) as MarcaDoNo[]).map((m) => {
          const n = contagem.get(m) ?? 0
          if (n === 0) return null
          return <Chip key={m} fundo={COR_DA_MARCA[m].fundo} texto={COR_DA_MARCA[m].texto}>{n} {COR_DA_MARCA[m].rotulo}</Chip>
        })}
        {curingas > 0 && <Chip fundo="var(--erro-soft)" texto="var(--erro-on-soft)">{curingas} fio(s) por curinga</Chip>}
        {grafo.repetidos.length > 0 && (
          <Chip fundo="var(--acao-soft)" texto="var(--acao-on-soft)">{grafo.repetidos.length} pino(s) alcançado(s) por dois caminhos</Chip>
        )}
        {contagem.size === 0 && curingas === 0 && grafo.repetidos.length === 0 && (
          <Chip fundo="var(--ok-soft)" texto="var(--ok-on-soft)">nenhum problema de junção aqui</Chip>
        )}
      </div>
    </section>
  )
}

function ListaDeAchados({ grafo, aoEscolher }: { grafo: GrafoDoPinout; aoEscolher: (id: string) => void }) {
  const comMarca = grafo.nos.filter((n) => n.achados.length > 0)
  if (comMarca.length === 0) {
    return (
      <p style={{ fontSize: 12.5, color: "var(--txt2)", margin: 0 }}>
        Clique num pino para ver as ações dele, ou no topo de uma caixa para marcá-la como junção.
        Arraste as caixas pelo topo para arrumar o desenho — o arranjo fica guardado.
      </p>
    )
  }
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <b style={{ fontSize: 12.5 }}>O que está errado neste desenho</b>
      {comMarca.map((n) => (
        <button
          key={n.id}
          onClick={() => aoEscolher(n.id)}
          style={{
            textAlign: "left", padding: 8, borderRadius: 7, border: "1px solid var(--line)",
            background: COR_DA_MARCA[n.achados[0]!].fundo, fontSize: 12, cursor: "pointer",
          }}
        >
          <b style={{ color: COR_DA_MARCA[n.achados[0]!].texto }}>
            {n.componenteCodigo || n.componente} · {n.pino} — {n.circuito || "(vazio)"}
          </b>
          <div style={{ color: "var(--txt2)", marginTop: 2 }}>
            {n.achados.map((m) => COR_DA_MARCA[m].explica).join("; ")}
          </div>
        </button>
      ))}
    </section>
  )
}

function PainelDoPino({
  no, grafo, trabalhando, aoNovaLigacao, aoEditarCadastro, aoLigarExistente, aoSoltar, aoApagar,
}: {
  no: NoDoGrafo
  grafo: GrafoDoPinout
  trabalhando: string | null
  aoNovaLigacao: () => void
  aoEditarCadastro: (entidade: "conectores" | "circuitos") => void
  aoLigarExistente: () => void
  aoSoltar: () => void
  aoApagar: () => void
}) {
  const [confirmandoApagar, setConfirmandoApagar] = useState(false)
  const achados = grafo.achados.filter((a) =>
    a.familia === "Juncao" ? a.pinoutId === no.pinoutId : a.circuitoId === no.circuitoId)
  const ligacoes = grafo.arestas.filter((a) => a.de === no.id || a.para === no.id)

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>
        <b style={{ fontSize: 13 }}>{no.componenteCodigo || no.componente} · pino {no.pino}</b>
        <div style={{ fontSize: 12, color: "var(--txt2)" }}>
          conector {no.conector} · circuito <code style={{ fontFamily: "var(--fm)" }}>{no.circuito || "(vazio)"}</code>
          {no.cor.trim() ? ` · ${no.cor.trim()}` : ""}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--txt3)" }}>{ligacoes.length} ligação(ões) no desenho</div>
      </div>

      {achados.map((a, i) => (
        <div key={i} style={{ fontSize: 12, padding: 8, borderRadius: 7, background: "var(--aviso-soft)", color: "var(--aviso-on-soft)" }}>
          {a.explicacao}
        </div>
      ))}

      {no.pinoutId > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <BotaoLargo aoClicar={aoNovaLigacao} ocupado={trabalhando !== null}>
            Nova ligação a partir deste pino…
          </BotaoLargo>
          <div style={{ display: "flex", gap: 6 }}>
            {no.circuitoId > 0 && (
              <BotaoLargo aoClicar={() => aoEditarCadastro("circuitos")} ocupado={trabalhando !== null}
                          titulo="O cadastro do circuito — renomear aqui vale para todas as pontas do fio">
                Editar circuito…
              </BotaoLargo>
            )}
            {no.conectorId > 0 && (
              <BotaoLargo aoClicar={() => aoEditarCadastro("conectores")} ocupado={trabalhando !== null}
                          titulo="O cadastro do conector — vale para todo pino dele">
                Editar conector…
              </BotaoLargo>
            )}
          </div>
          <BotaoLargo aoClicar={aoLigarExistente} ocupado={trabalhando !== null}>
            Ligar a um pino que já está no desenho…
          </BotaoLargo>
          {temEtiqueta(no.circuito) && (
            <BotaoLargo aoClicar={aoSoltar} ocupado={trabalhando !== null}
                        titulo={`O circuito passa a '${base(no.circuito)}'. A linha e a ligação ficam.`}>
              Soltar a etiqueta {etiqueta(no.circuito)} (vai para o lote)
            </BotaoLargo>
          )}
          {!confirmandoApagar ? (
            <BotaoLargo aoClicar={() => setConfirmandoApagar(true)} ocupado={trabalhando !== null} discreto>
              Apagar esta linha…
            </BotaoLargo>
          ) : (
            <div style={{ padding: 8, borderRadius: 7, border: "1px solid var(--erro)", fontSize: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              <span>
                Apaga o pinout <b>{no.componenteCodigo || no.componente} pino {no.pino}</b> quando o lote for gravado. Se o
                problema é o nome do circuito, soltar a etiqueta resolve sem perder a linha. Se apagar deixar uma junção
                órfã, o lote é recusado.
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <BotaoPequeno aoClicar={() => setConfirmandoApagar(false)}>Não apagar</BotaoPequeno>
                <BotaoPequeno aoClicar={() => { setConfirmandoApagar(false); aoApagar() }} perigo>Pôr no lote: apagar</BotaoPequeno>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function PainelDaCaixa({ caixa, aoMarcar, aoNovoFio, aoNovaJuncao, aoEditarComponente }: {
  caixa: Caixa; aoMarcar: (ids: number[]) => void; aoNovoFio: () => void; aoNovaJuncao: () => void; aoEditarComponente: () => void
}) {
  const candidatos = caixa.pinos.map((p) => p.no).filter((n) => n.pinoutId > 0)
  const [marcados, setMarcados] = useState<Set<number>>(() => new Set(candidatos.map((n) => n.pinoutId)))
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>
        <b style={{ fontSize: 13 }}>{caixa.codigo || caixa.titulo}</b>
        <div style={{ fontSize: 12, color: "var(--txt2)" }}>
          {caixa.titulo} · {caixa.pinos.length} pino(s){caixa.tipo === 3 ? " · marcado como junção" : ""}
        </div>
      </div>
      <BotaoLargo aoClicar={aoEditarComponente}>Editar componente…</BotaoLargo>
      <BotaoLargo aoClicar={aoNovoFio}>Novo fio saindo daqui…</BotaoLargo>
      <BotaoLargo aoClicar={aoNovaJuncao}>Nova junção neste componente…</BotaoLargo>
      <b style={{ fontSize: 12.5 }}>Marcar como junção</b>
      <span style={{ fontSize: 12, color: "var(--txt2)" }}>
        Escolha os ramos. O sistema põe o componente como Junção (tipo 3) e cada ramo num fio
        próprio com # no fim (E46T36.E1 → E46T36.E1#); a ponta distante continua no nome sem #. Os
        ramos precisam começar igual antes do ponto (E46T36.E, E46T36.E1…).
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 260, overflow: "auto" }}>
        {candidatos.map((n) => (
          <label key={n.id} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12 }}>
            <input
              type="checkbox"
              checked={marcados.has(n.pinoutId)}
              onChange={(e) => setMarcados((s) => {
                const novo = new Set(s)
                if (e.target.checked) novo.add(n.pinoutId)
                else novo.delete(n.pinoutId)
                return novo
              })}
            />
            <span style={{ width: 70, color: "var(--txt2)" }}>{n.pino}</span>
            <code style={{ fontFamily: "var(--fm)" }}>{n.circuito || "(vazio)"}</code>
          </label>
        ))}
      </div>
      <BotaoLargo aoClicar={() => aoMarcar([...marcados])} ocupado={marcados.size < 2}>
        Pôr no lote: junção com {marcados.size} ramo(s)
      </BotaoLargo>
    </section>
  )
}

function Lote({ pendentes, resultado, trabalhando, aoTirar, aoSimular, aoGravar }: {
  pendentes: Pendente[]
  resultado: ResultadoDaEdicao | null
  trabalhando: string | null
  aoTirar: (i: number) => void
  aoSimular: () => void
  aoGravar: () => void
}) {
  if (pendentes.length === 0 && !resultado) return null
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
      <b style={{ fontSize: 12.5 }}>Alterações no lote ({pendentes.length})</b>
      {pendentes.map((p, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12 }}>
          <span style={{ flex: 1 }}>{p.texto}</span>
          <BotaoPequeno aoClicar={() => aoTirar(i)} titulo="Tirar do lote">×</BotaoPequeno>
        </div>
      ))}
      {pendentes.length > 0 && (
        <div style={{ display: "flex", gap: 6 }}>
          <BotaoLargo aoClicar={aoSimular} ocupado={trabalhando !== null}
                      titulo="Aplica, confere e desfaz — mostra o que aconteceria, sem gravar">
            Simular
          </BotaoLargo>
          <BotaoLargo aoClicar={aoGravar} ocupado={trabalhando !== null} destaque>
            {trabalhando ?? "Gravar"}
          </BotaoLargo>
        </div>
      )}
      {resultado && <Resultado r={resultado} />}
    </section>
  )
}

function Resultado({ r }: { r: ResultadoDaEdicao }) {
  const recusado = r.recusas.length > 0
  const titulo = recusado ? "Nada foi gravado" : r.simulado ? "Simulação: daria certo" : "Gravado"
  const fundo = recusado ? "var(--erro-soft)" : r.simulado ? "var(--brand-soft)" : "var(--ok-soft)"
  const texto = recusado ? "var(--erro-on-soft)" : r.simulado ? "var(--brand-on-soft)" : "var(--ok-on-soft)"
  return (
    <div role="status" style={{ padding: 10, borderRadius: 7, background: fundo, color: texto, fontSize: 12, display: "flex", flexDirection: "column", gap: 5 }}>
      <b>{titulo}</b>
      {r.recusas.map((x, i) => <span key={i}>{x}</span>)}
      {r.alteracoes.map((x, i) => <span key={i}>{r.aplicado ? "✓ " : "→ "}{x}</span>)}
      {(r.avisos ?? []).map((x, i) => <span key={`a${i}`} style={{ fontWeight: 600 }}>⚠ {x}</span>)}
      {!recusado && (
        <span style={{ opacity: 0.85 }}>
          Problemas de junção no projeto: órfãs {r.antes.juncaoOrfa} → {r.depois.juncaoOrfa}, curinga {r.antes.curinga} → {r.depois.curinga},
          rede larga {r.antes.redeLarga} → {r.depois.redeLarga}.
        </span>
      )}
    </div>
  )
}

// ================================================================= peças

function cortar(texto: string, max: number) {
  return texto.length > max ? texto.slice(0, max - 1) + "…" : texto
}

function Centro({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: 8, fontSize: 13, color: "var(--txt2)", textAlign: "center", padding: 20,
    }}>
      {children}
    </div>
  )
}

function Chip({ children, fundo, texto }: { children: React.ReactNode; fundo: string; texto: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", height: 22, padding: "0 8px", borderRadius: 999,
      background: fundo, color: texto, fontSize: 11.5, fontWeight: 600,
    }}>
      {children}
    </span>
  )
}

function BotaoPequeno({ children, aoClicar, titulo, perigo }: {
  children: React.ReactNode; aoClicar: () => void; titulo?: string; perigo?: boolean
}) {
  return (
    <button
      onClick={aoClicar}
      title={titulo}
      style={{
        height: 28, padding: "0 10px", borderRadius: 7, fontSize: 12,
        border: `1px solid ${perigo ? "var(--erro)" : "var(--line2)"}`,
        background: perigo ? "var(--erro)" : "var(--surface)", color: perigo ? "#FFFFFF" : "var(--txt2)",
      }}
    >
      {children}
    </button>
  )
}

function BotaoLargo({ children, aoClicar, ocupado, destaque, discreto, titulo }: {
  children: React.ReactNode; aoClicar: () => void; ocupado?: boolean; destaque?: boolean; discreto?: boolean; titulo?: string
}) {
  return (
    <button
      onClick={aoClicar}
      disabled={ocupado}
      title={titulo}
      style={{
        flex: 1, minHeight: 32, padding: "6px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 600, textAlign: "left",
        border: destaque ? "none" : "1px solid var(--line2)",
        background: destaque ? "var(--brand)" : "var(--surface)",
        color: destaque ? "#FFFFFF" : discreto ? "var(--txt3)" : "var(--txt)",
        opacity: ocupado ? 0.5 : 1, cursor: ocupado ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  )
}
